import { keepPreviousData, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { appelApi, urlApi } from './api';
import type { SourceSousTitres, StatutCours } from './useCours';

// ---------------------------------------------------------------------------
// Lecteur de cours (écran `player`, TF-444) : les cours en ligne publiés de la
// formation de l'apprenant, et la leçon qu'il lit. L'auteur et la direction
// ouvrent la leçon en aperçu ; qui lit quoi est tranché par l'API.
// ---------------------------------------------------------------------------

/**
 * Avancement d'un module, d'un cours, d'un bloc ou de la formation (TF-451) :
 * leçons terminées et quiz faits. `taux` est arrondi à l'inférieur par l'API ;
 * `null` : rien à faire.
 */
export interface Avancement {
  faits: number;
  total: number;
  taux: number | null;
}

export interface LeconProgramme {
  id: string;
  titre: string;
  video: boolean;
  dureeSecondes: number | null;
  /** Nombre de ressources annexes (TF-447). */
  ressources: number;
  /** L'apprenant l'a terminée (TF-451) ; `null` en aperçu, où rien n'est suivi. */
  terminee: boolean | null;
}

export type NatureRessource = 'PDF' | 'ZIP' | 'LIEN';

/** Ressource annexe d'une leçon (TF-447) : un fichier servi par le lecteur, ou un lien. */
export interface RessourceEnLecture {
  id: string;
  nature: NatureRessource;
  titre: string;
  nomFichier: string | null;
  tailleOctets: number | null;
  adresse: string | null;
  domaine: string | null;
}

/** Devoir ou quiz d'un module, ou de la fin du cours (TF-454). */
export interface DevoirDuProgramme {
  id: string;
  type: 'QUIZ' | 'PROJET' | 'RAPPORT' | 'AUTRE';
  titre: string;
  echeance: string;
  /** Où en est l'apprenant ; `null` en aperçu. */
  statut: 'A_RENDRE' | 'RENDU' | 'CORRIGE' | null;
  enRetard: boolean | null;
  /** Quiz commencé, pas encore fini. */
  enCours: boolean;
  /** Note sur 20 d'un quiz fait. */
  note: number | null;
  /** Aperçu : un brouillon que l'apprenant ne voit pas. */
  brouillon: boolean;
}

export interface ModuleProgramme {
  id: string;
  titre: string;
  lecons: LeconProgramme[];
  /** `null` en aperçu. Ses devoirs n'y comptent pas. */
  avancement: Avancement | null;
  /** Ses devoirs et quiz, après ses leçons (TF-454). */
  devoirs: DevoirDuProgramme[];
}

/** Bloc du programme de la formation que couvre un cours (TF-451). */
export interface BlocDuCours {
  id: string;
  libelle: string;
  numero: number;
  total: number;
}

/** Où reprendre un cours (TF-449) : la dernière leçon regardée, ou la suivante si elle est vue. */
export interface LeconAReprendre {
  id: string;
  titre: string;
  numero: string;
  /** `null` : la leçon s'ouvre au début. */
  positionSecondes: number | null;
}

export interface CoursEnLigne {
  id: string;
  titre: string;
  description: string | null;
  auteur: string;
  /** `null` : cours transversal. */
  bloc: BlocDuCours | null;
  avancement: Avancement;
  modules: ModuleProgramme[];
  /** Devoirs et quiz de fin de cours (TF-454). */
  evaluations: DevoirDuProgramme[];
  /** `null` tant qu'aucune leçon du cours n'a été regardée. */
  reprise: LeconAReprendre | null;
  /** La leçon qu'ouvre le cours : sa reprise, sinon sa première leçon ; `null` sans leçon. */
  prochaineLecon: LeconAReprendre | null;
}

export interface MesCoursEnLigne {
  refus: string | null;
  formation: string | null;
  cours: CoursEnLigne[];
}

export interface LeconVoisine {
  id: string;
  titre: string;
}

export interface VueLecon {
  acces: 'APPRENANT' | 'APERCU';
  cours: {
    id: string;
    titre: string;
    description: string | null;
    statut: StatutCours;
    statutLibelle: string;
    formation: string;
    auteur: string;
  };
  /** Leçons terminées du cours (TF-451) ; `null` en aperçu. */
  avancement: Avancement | null;
  lecon: {
    id: string;
    titre: string;
    numero: string;
    /** `null` en aperçu. */
    terminee: boolean | null;
    module: { id: string; titre: string };
    video: { id: string; dureeSecondes: number | null } | null;
    sousTitres: { version: string } | null;
    ressources: RessourceEnLecture[];
    /**
     * Position de lecture suivie (TF-449) : l'apprenant, sur une leçon qui a
     * sa vidéo ; `null` sinon, et rien n'est envoyé. `repriseSecondes` : où la
     * vidéo reprend, `null` du début.
     */
    visionnage: { repriseSecondes: number | null } | null;
  };
  programme: ModuleProgramme[];
  /** Devoirs et quiz de fin de cours (TF-454). */
  evaluations: DevoirDuProgramme[];
  precedente: LeconVoisine | null;
  suivante: LeconVoisine | null;
  /** Questions de la discussion du module que ce lecteur voit (TF-448). */
  discussion: { questions: number };
}

/** Une réplique de la transcription, en millisecondes. */
export interface Replique {
  debutMs: number;
  finMs: number;
  texte: string;
}

/** Transcription d'une leçon (TF-445) : le texte horodaté qui sert aussi de sous-titres. */
export interface VueTranscription {
  etat: 'DISPONIBLE' | 'EN_PREPARATION' | 'AUCUNE';
  version: string | null;
  source: SourceSousTitres | null;
  corrigeeLe: string | null;
  repliques: Replique[];
  /** Aperçu : une nouvelle transcription remplacera ce texte. */
  remplacementEnCours: boolean;
  /** Aperçu de l'auteur ou de la direction : ce qui bloque la correction, ou `null`. Absent pour l'apprenant. */
  correction: { refus: string | null } | null;
}

/** Une transcription en préparation est relue de temps en temps. */
const SUIVI_PREPARATION_MS = 30_000;

export function useTranscriptionLecon(leconId: string, actif: boolean) {
  return useQuery({
    queryKey: ['lecteur', 'lecon', leconId, 'transcription'],
    queryFn: () => appelApi<VueTranscription>(`/lecteur/lecons/${encodeURIComponent(leconId)}/transcription`),
    enabled: actif,
    refetchInterval: (requete) =>
      requete.state.data?.etat === 'EN_PREPARATION' || requete.state.data?.remplacementEnCours
        ? SUIVI_PREPARATION_MS
        : false,
  });
}

export function useMesCoursEnLigne(actif: boolean) {
  return useQuery({
    queryKey: ['lecteur', 'cours'],
    queryFn: () => appelApi<MesCoursEnLigne>('/lecteur/cours'),
    enabled: actif,
  });
}

// Oubliée dès qu'on quitte la leçon : rouverte, elle est relue avant que la
// vidéo démarre, avec la position où la reprendre (TF-449), même regardée
// entre-temps sur un autre appareil.
export function useLeconLecteur(leconId: string | null) {
  return useQuery({
    queryKey: ['lecteur', 'lecon', leconId ?? ''],
    queryFn: () => appelApi<VueLecon>(`/lecteur/lecons/${encodeURIComponent(leconId ?? '')}`),
    enabled: Boolean(leconId),
    gcTime: 0,
  });
}

/** Une note personnelle de l'apprenant (TF-446), prise à un moment de la vidéo. */
export interface NoteLecon {
  id: string;
  momentSecondes: number;
  texte: string;
  creeLe: string;
  modifieLe: string;
  /** La vidéo a été remplacée ou retirée depuis la prise de note. */
  videoChangee: boolean;
}

export interface NotesDeLaLecon {
  /** Ce qui empêche d'ajouter une note (aperçu, leçon sans vidéo), ou `null`. */
  refusAjout: string | null;
  notes: NoteLecon[];
}

export interface LeconAvecNotes {
  id: string;
  titre: string;
  numero: string;
  module: string;
  /** La leçon s'ouvre encore dans le lecteur. */
  lisible: boolean;
  notes: NoteLecon[];
}

/** « Mes notes » : toutes les notes du compte, par cours puis par leçon. */
export interface MesNotes {
  total: number;
  cours: { id: string; titre: string; lecons: LeconAvecNotes[] }[];
}

// Notes reprises au retour sur l'onglet : prises sur un autre appareil, elles y apparaissent.
export function useNotesLecon(leconId: string, actif: boolean) {
  return useQuery({
    queryKey: ['lecteur', 'notes', 'lecon', leconId],
    queryFn: () => appelApi<NotesDeLaLecon>(`/lecteur/lecons/${encodeURIComponent(leconId)}/notes`),
    enabled: actif,
    refetchOnWindowFocus: true,
  });
}

export function useMesNotes(recherche: string, actif: boolean) {
  return useQuery({
    queryKey: ['lecteur', 'notes', 'toutes', recherche],
    queryFn: () =>
      appelApi<MesNotes>(`/lecteur/notes${recherche ? `?recherche=${encodeURIComponent(recherche)}` : ''}`),
    enabled: actif,
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
  });
}

function apresNote(client: QueryClient): void {
  void client.invalidateQueries({ queryKey: ['lecteur', 'notes'] });
}

export function useAjouterNote(leconId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (note: { momentSecondes: number; texte: string }) =>
      appelApi<NoteLecon>(`/lecteur/lecons/${encodeURIComponent(leconId)}/notes`, { methode: 'POST', corps: note }),
    onSuccess: () => apresNote(client),
  });
}

export function useModifierNote() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, texte }: { id: string; texte: string }) =>
      appelApi<NoteLecon>(`/lecteur/notes/${encodeURIComponent(id)}`, { methode: 'PATCH', corps: { texte } }),
    onSuccess: () => apresNote(client),
  });
}

export function useSupprimerNote() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => appelApi<void>(`/lecteur/notes/${encodeURIComponent(id)}`, { methode: 'DELETE' }),
    onSettled: () => apresNote(client),
  });
}

export type RoleDiscussion = 'APPRENANT' | 'FORMATEUR' | 'DIRECTION';

export type GesteMessage = 'MASQUER' | 'REAFFICHER' | 'SUPPRIMER';

/** Un message de la discussion d'un module (TF-448), avec les gestes de qui le lit. */
export interface MessageDiscussion {
  id: string;
  texte: string;
  creeLe: string;
  auteur: { nom: string; initiales: string; role: RoleDiscussion | null; badge: string | null };
  /** Message du formateur ou de la direction. */
  miseEnAvant: boolean;
  deMoi: boolean;
  masque: { le: string; mention: string } | null;
  gestes: { geste: GesteMessage; libelle: string; refus: string | null }[];
}

export interface QuestionDiscussion extends MessageDiscussion {
  titre: string;
  lecon: { id: string; numero: string; titre: string } | null;
  refusReponse: string | null;
  reponses: MessageDiscussion[];
}

export interface VueDiscussion {
  module: { id: string; titre: string; numero: number };
  refusQuestion: string | null;
  moderation: boolean;
  questions: QuestionDiscussion[];
}

/** Le fil se reprend toutes les 30 s tant qu'il est ouvert : les réponses des autres y arrivent. */
export function useDiscussionLecon(leconId: string, actif: boolean) {
  return useQuery({
    queryKey: ['lecteur', 'discussion', leconId],
    queryFn: () => appelApi<VueDiscussion>(`/lecteur/lecons/${encodeURIComponent(leconId)}/discussion`),
    enabled: actif,
    refetchInterval: actif ? 30_000 : false,
    refetchOnWindowFocus: true,
  });
}

/** Chaque geste renvoie le fil à jour ; le compte de questions de l'onglet suit. */
function useGesteDiscussion<V>(leconId: string, envoyer: (variables: V) => Promise<VueDiscussion>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: envoyer,
    onSuccess: (fil) => {
      client.setQueryData(['lecteur', 'discussion', leconId], fil);
      void client.invalidateQueries({ queryKey: ['lecteur', 'lecon', leconId], exact: true });
    },
    onError: () => {
      void client.invalidateQueries({ queryKey: ['lecteur', 'discussion', leconId] });
    },
  });
}

function adresseDiscussion(leconId: string, messageId?: string): string {
  return `/lecteur/lecons/${encodeURIComponent(leconId)}/discussion${messageId ? `/${encodeURIComponent(messageId)}` : ''}`;
}

export function usePoserQuestion(leconId: string) {
  return useGesteDiscussion(leconId, (question: { titre: string; texte: string }) =>
    appelApi<VueDiscussion>(adresseDiscussion(leconId), { methode: 'POST', corps: question }),
  );
}

export function useRepondreQuestion(leconId: string) {
  return useGesteDiscussion(leconId, ({ questionId, texte }: { questionId: string; texte: string }) =>
    appelApi<VueDiscussion>(`${adresseDiscussion(leconId, questionId)}/reponses`, { methode: 'POST', corps: { texte } }),
  );
}

export function useGesteMessage(leconId: string) {
  return useGesteDiscussion(leconId, ({ messageId, geste }: { messageId: string; geste: GesteMessage }) =>
    geste === 'SUPPRIMER'
      ? appelApi<VueDiscussion>(adresseDiscussion(leconId, messageId), { methode: 'DELETE' })
      : appelApi<VueDiscussion>(`${adresseDiscussion(leconId, messageId)}/moderation`, { methode: 'POST', corps: { geste } }),
  );
}

/** L'adresse change avec la vidéo : une vidéo remplacée se recharge. */
export function urlVideoLecon(leconId: string, videoId: string): string {
  return urlApi(`/lecteur/lecons/${encodeURIComponent(leconId)}/video?v=${encodeURIComponent(videoId)}`);
}

export function urlSousTitresLecon(leconId: string, version: string): string {
  return urlApi(`/lecteur/lecons/${encodeURIComponent(leconId)}/sous-titres?v=${encodeURIComponent(version)}`);
}

/**
 * Fichier d'une ressource : `enLigne` pour la consulter dans le lecteur,
 * sinon le téléchargement sous son nom d'origine. Chaque lecture est tracée.
 */
export function urlRessource(ressourceId: string, enLigne = false): string {
  return urlApi(`/lecteur/ressources/${encodeURIComponent(ressourceId)}/fichier${enLigne ? '?inline=1' : ''}`);
}

/** « 240 Ko », « 2,4 Mo » : le poids d'une ressource, plus fin que celui d'une vidéo. */
export function tailleRessource(octets: number | null): string | null {
  if (!octets) return null;
  if (octets < 1024 ** 2) return `${Math.max(1, Math.round(octets / 1024))} Ko`;
  return `${(octets / 1024 ** 2).toFixed(1).replace('.', ',')} Mo`;
}

export function nombreDeRessources(nombre: number): string {
  return `${nombre} ressource${nombre > 1 ? 's' : ''}`;
}

export type OngletLecon = 'transcription' | 'notes' | 'ressources' | 'discussion';

/** Adresse d'une leçon dans le lecteur ; `onglet` ouvre un onglet sous la vidéo, `depart` y fait commencer la lecture. */
export function adresseLecon(leconId: string, onglet?: OngletLecon, depart?: number): string {
  return `/player?lecon=${encodeURIComponent(leconId)}${onglet ? `&onglet=${onglet}` : ''}${
    depart !== undefined ? `&t=${Math.max(0, Math.floor(depart))}` : ''
  }`;
}

/** Une question de la discussion, ouverte dans le lecteur depuis cette leçon. */
export function adresseQuestion(leconId: string, questionId: string): string {
  return `${adresseLecon(leconId, 'discussion')}&question=${encodeURIComponent(questionId)}`;
}
